import { CommonModule } from '@angular/common';
import {} from '@angular/common/http';
import {
    AfterViewChecked,
    AfterViewInit,
    OnDestroy,
    Component,
    HostListener,
    inject,
    OnInit,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { DatiqAppConfig } from '@dat/datiq-core/models';
import { DatiqDatadogRumService } from '@dat/datiq-observability';
import { DatDbClientService } from '@dat/db';
import {
    IqEventBusBrokerService,
    IqEventBusRegisterComponent,
} from '@dat/iq-event-bus';
import { IqRulesRegisterComponent } from '@dat/iq-rules';
import { RumInitConfiguration } from '@datadog/browser-rum';
import { lastValueFrom, Observable, Subscription, tap } from 'rxjs';
import { environmentVariables } from '../../config/environment.variables';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        IqEventBusRegisterComponent,
        IqRulesRegisterComponent,
    ],
    providers: [],
    selector: 'mfe-trendlines-builder',
    template: ` <iq-event-bus-register>
        <iq-rules-register />
        <router-outlet></router-outlet
    ></iq-event-bus-register>`,
})
export class RemoteEntryComponent
    implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy
{
    private _events: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _db: DatDbClientService = inject(DatDbClientService);
    private _appconfig: DatiqAppConfig = inject(DatiqAppConfig);

    private _dbOpts: { dbName: string } = { dbName: 'trendlines-cache' };
    private CACHE_BREAKER_DOC = 'trendlines_cache_breaker';
    private _datadogRum: DatiqDatadogRumService = inject(DatiqDatadogRumService);

    private destroyDb$: Observable<boolean> = this._db
        .deleteDb(this._dbOpts)
        .pipe(
            tap((purged) => {
                if (purged) {
                    this._setCacheBreaker(this._getCacheBreakerQuery());
                }
            }),
        );
    private subscription: Subscription;

    constructor() {
        this._resolveCacheBreaker();
    }

    ngOnInit(): void {
        const rumConfig: RumInitConfiguration = {
            applicationId: environmentVariables.DATADOG_RUM_APPLICATION_ID,
            clientToken: environmentVariables.DATADOG_RUM_CLIENT_TOKEN,
            env: environmentVariables.DATADOG_RUM_ENVIRONMENT,
            service: environmentVariables.DATADOG_RUM_SERVICE_NAME,
            allowedTracingUrls: [
                (url) =>
                    url.startsWith(
                        environmentVariables.DATADOG_RUM_TRACING_URL,
                    ),
            ],
        };
        this._datadogRum.init(rumConfig);
    }

    ngAfterViewInit(): void {
        this._events.on('iframePostMessage', (payload: any) => {
            if (window.self !== window.top) {
                window.parent.postMessage(payload, '*');
            }
        });
    }

    ngAfterViewChecked(): void {
        this.sendScrollHeight(false);
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any): void {
        this.sendScrollHeight(true);
    }

    sendScrollHeight(resize: boolean): void {
        const body = document.body;
        const scrollHeight = body.scrollHeight;
        const height = !resize ? Math.max(scrollHeight, 1080) : scrollHeight; //get the iFrames height when content is loaded or set height to 1080px(100vh) when content is loading
        this.postScrollMessageToParent(height);
    }

    postScrollMessageToParent(scrollHeight: number): void {
        if (window.parent) {
            window.parent.postMessage(
                {
                    type: 'SCROLL_HEIGHT',
                    height: scrollHeight,
                },
                '*',
            );
        }
    }

    private async _resolveCacheBreaker(): Promise<void> {
        const configuredCacheBreaker = this._getCacheBreakerQuery();
        try {
            this._intializeDb();
            this._validateCacheBreaker(configuredCacheBreaker);
        } catch (error) {
            console.error('Error checking cache breaker:', error);
            return;
        }
    }

    private _intializeDb(): void {
        this._db.createDBs(['trendlines-cache']);
    }

    private async _validateCacheBreaker(
        configuredCacheBreaker: string,
    ): Promise<void> {
        const cacheBreakerExists = await lastValueFrom(
            this._db.hasDoc(this.CACHE_BREAKER_DOC, this._dbOpts),
        );
        if (!cacheBreakerExists) {
            this._purgeAndSetCacheBreaker();
            return;
        }
        const cacheBreakerDoc = (await lastValueFrom(
            this._db.getDoc(this.CACHE_BREAKER_DOC, this._dbOpts),
        )) as { query: string };

        if (
            !this._isCacheQueryValid(
                configuredCacheBreaker,
                cacheBreakerDoc.query,
            )
        ) {
            this._purgeAndSetCacheBreaker();
            return;
        }
        return;
    }

    private _purgeAndSetCacheBreaker(): void {
        this.subscription = this.destroyDb$.subscribe();
    }

    private _setCacheBreaker(query: string): void {
        this._intializeDb();
        this._db.setDoc(this.CACHE_BREAKER_DOC, { query }, this._dbOpts);
    }

    private _getCacheBreakerQuery(): string {
        return this._appconfig?.coreConfig?.CACHE_BREAKER_QUERY ?? '';
    }
    private _isCacheQueryValid(
        configQuery: string,
        cacheQuery: string,
    ): boolean {
        return configQuery === cacheQuery;
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
